import React, { useState, useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { Card, Button, Input, Modal, Select } from '@components/common/UIComponents';
import {
  addWaterEntry,
  getWaterForDate,
  getWaterInRange,
  deleteWaterEntry,
  getUserSettings
} from '@services/firestoreService';
import { Droplets, Plus, Trash2, ChevronLeft, ChevronRight, Award } from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, eachDayOfInterval } from 'date-fns';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const HydrationPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [waterEntries, setWaterEntries] = useState([]); // Today's entries
  const [periodData, setPeriodData] = useState([]); // Period entries for charts
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [waterGoal, setWaterGoal] = useState(3000); // Default 3L

  useEffect(() => {
    loadWaterData();
  }, [user, period, currentDate]);

  const getPeriodRange = () => {
    if (period === 'day') {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate)
      };
    } else if (period === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), // Monday
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    }
  };

  const loadWaterData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load user settings for goal
      const settings = await getUserSettings(user.uid);
      if (settings?.waterGoal) {
        setWaterGoal(settings.waterGoal);
      }

      // Load today's entries
      const todayEntries = await getWaterForDate(user.uid, new Date());
      setWaterEntries(todayEntries);

      // Load period data
      const { start, end } = getPeriodRange();
      const periodEntries = await getWaterInRange(user.uid, start, end);
      setPeriodData(periodEntries);
    } catch (error) {
      console.error('Error loading water data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (period === 'day') {
      setCurrentDate(direction === 'prev' ? subDays(currentDate, 1) : addDays(currentDate, 1));
    } else if (period === 'week') {
      setCurrentDate(direction === 'prev' ? subWeeks(currentDate, 1) : addWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'prev' ? subMonths(currentDate, 1) : addMonths(currentDate, 1));
    }
  };

  const getPeriodLabel = () => {
    if (period === 'day') {
      return format(currentDate, 'MMMM dd, yyyy');
    } else if (period === 'week') {
      const { start, end } = getPeriodRange();
      return `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`;
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  };

  const handleQuickAdd = async (amount) => {
    try {
      await addWaterEntry(user.uid, amount);
      loadWaterData();
    } catch (error) {
      console.error('Error adding water:', error);
    }
  };

  const handleCustomAdd = async () => {
    if (!customAmount || customAmount <= 0) return;

    try {
      await addWaterEntry(user.uid, parseInt(customAmount));
      setShowCustomModal(false);
      setCustomAmount('');
      loadWaterData();
    } catch (error) {
      console.error('Error adding custom water:', error);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await deleteWaterEntry(user.uid, entryId);
      loadWaterData();
    } catch (error) {
      console.error('Error deleting water entry:', error);
    }
  };

  const totalWater = waterEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const progress = (totalWater / waterGoal) * 100;

  // Group entries by hour
  const entriesByHour = waterEntries.reduce((acc, entry) => {
    const hour = new Date(entry.date).getHours();
    if (!acc[hour]) acc[hour] = [];
    acc[hour].push(entry);
    return acc;
  }, {});

  // Calculate period statistics
  const { start, end } = getPeriodRange();
  const allDays = eachDayOfInterval({ start, end });
  
  // Group period data by day
  const waterByDay = allDays.map(day => {
    const dayId = format(day, 'yyyy-MM-dd');
    const dayEntries = periodData.filter(entry => {
      const entryDate = entry.date ? new Date(entry.date) : new Date();
      return format(entryDate, 'yyyy-MM-dd') === dayId;
    });
    
    const dayTotal = dayEntries.reduce((sum, e) => sum + (e.amount || 0), 0);
    
    return {
      date: day,
      dateId: dayId,
      amount: dayTotal,
      entries: dayEntries
    };
  });

  // Calculate statistics
  const waterStats = {
    total: waterByDay.reduce((sum, day) => sum + day.amount, 0),
    average: Math.round(waterByDay.reduce((sum, day) => sum + day.amount, 0) / waterByDay.length) || 0,
    max: Math.max(...waterByDay.map(day => day.amount), 0),
    daysAchieved: waterByDay.filter(day => day.amount >= waterGoal).length
  };

  // Prepare chart data
  const getChartLabels = () => {
    if (period === 'day') {
      return ['Today'];
    } else if (period === 'week') {
      return allDays.map(day => format(day, 'EEE')); // Mon, Tue, Wed...
    } else {
      // For month, show every few days
      return allDays.filter((_, index) => index % Math.ceil(allDays.length / 10) === 0)
                    .map(day => format(day, 'MMM d'));
    }
  };

  const getChartData = () => {
    if (period === 'day') {
      const todayData = waterByDay.find(day => 
        format(day.date, 'yyyy-MM-dd') === format(currentDate, 'yyyy-MM-dd')
      );
      return [todayData?.amount || 0];
    }
    
    return waterByDay.map(day => day.amount);
  };

  const waterChartData = {
    labels: getChartLabels(),
    datasets: [
      {
        label: 'Water (ml)',
        data: getChartData(),
        backgroundColor: waterByDay.map(day => 
          day.amount >= waterGoal ? 'rgba(14, 165, 233, 0.8)' : 'rgba(156, 163, 175, 0.5)'
        ),
        borderColor: waterByDay.map(day => 
          day.amount >= waterGoal ? 'rgb(14, 165, 233)' : 'rgb(156, 163, 175)'
        ),
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          afterLabel: () => `Goal: ${waterGoal} ml`
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value} ml`
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Hydration Tracking</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Stay hydrated throughout the day
          </p>
        </div>
      </div>

      {/* Period Selector */}
      <Card>
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Period Tabs */}
          <div className="flex gap-2">
            <Button
              variant={period === 'day' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('day')}
            >
              Daily
            </Button>
            <Button
              variant={period === 'week' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('week')}
            >
              Weekly
            </Button>
            <Button
              variant={period === 'month' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod('month')}
            >
              Monthly
            </Button>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => handleNavigate('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[200px] text-center">
              {getPeriodLabel()}
            </span>
            <button
              onClick={() => handleNavigate('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              disabled={format(currentDate, 'yyyy-MM-dd') >= format(new Date(), 'yyyy-MM-dd')}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </Card>

      {/* Today's Progress */}
      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 bg-blue-100 dark:bg-blue-900/20 rounded-xl">
            <Droplets className="text-blue-600 dark:text-blue-400" size={32} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Today's Water Intake
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {format(new Date(), 'MMMM dd, yyyy')}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-end justify-between mb-2">
            <span className="text-5xl font-bold text-gray-900 dark:text-gray-100">
              {totalWater}
              <span className="text-2xl text-gray-500 dark:text-gray-400 ml-2">ml</span>
            </span>
            <span className="text-lg text-gray-600 dark:text-gray-400">
              / {waterGoal} ml
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
            <div
              className="bg-gradient-to-r from-blue-400 to-blue-600 h-4 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {progress >= 100 ? '🎉 Daily goal achieved!' : `${Math.round(progress)}% of daily goal`}
          </p>
        </div>

        {/* Quick Add Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button onClick={() => handleQuickAdd(250)} variant="outline" fullWidth>
            <Droplets size={16} className="mr-2" />
            250 ml
          </Button>
          <Button onClick={() => handleQuickAdd(500)} variant="outline" fullWidth>
            <Droplets size={16} className="mr-2" />
            500 ml
          </Button>
          <Button onClick={() => handleQuickAdd(750)} variant="outline" fullWidth>
            <Droplets size={16} className="mr-2" />
            750 ml
          </Button>
          <Button onClick={() => setShowCustomModal(true)} variant="outline" fullWidth>
            <Plus size={16} className="mr-2" />
            Custom
          </Button>
        </div>
      </Card>

      {/* Period Statistics */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Hydration Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {(waterStats.total / 1000).toFixed(1)}L
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Total Water</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {waterStats.average} ml
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Avg/Day</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {waterStats.max} ml
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Best Day</div>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-2xl font-bold text-blue-600 dark:text-blue-400">
                <Award size={24} />
                <span>{waterStats.daysAchieved}</span>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days Met Goal</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Water Intake Chart */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Water Intake Overview
        </h3>
        <div className="h-64">
          <Bar data={waterChartData} options={chartOptions} />
        </div>
      </Card>

      {/* Today's Log */}
      <Card>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Today's Log ({waterEntries.length} entries)
        </h3>

        {waterEntries.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Droplets size={48} className="mx-auto mb-3 opacity-30" />
            <p>No water logged yet today</p>
            <p className="text-sm mt-1">Start by adding your first entry above</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(entriesByHour)
              .sort(([hourA], [hourB]) => parseInt(hourB) - parseInt(hourA))
              .map(([hour, entries]) => (
                <div key={hour} className="border-l-4 border-blue-500 pl-4 py-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    {format(new Date().setHours(parseInt(hour), 0), 'h:00 a')}
                  </p>
                  <div className="space-y-2">
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Droplets size={20} className="text-blue-600 dark:text-blue-400" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {entry.amount} ml
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(entry.date), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        )}
      </Card>

      {/* Custom Amount Modal */}
      <Modal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        title="Add Custom Amount"
      >
        <div className="space-y-4">
          <Input
            type="number"
            label="Amount (ml)"
            placeholder="750"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            fullWidth
          />
          <div className="flex gap-3">
            <Button onClick={handleCustomAdd} fullWidth>
              Add
            </Button>
            <Button variant="secondary" onClick={() => setShowCustomModal(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HydrationPage;
